import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Tooltip,
  Chip,
  Alert,
  LinearProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from "@mui/icons-material";

/**
 * RoomImageModal
 * Props:
 *   roomId         {number}   - the room whose images we manage
 *   onClose        {function} - called when modal should close
 *   onImagesChange {function} - optional, called after mutation so parent refreshes badge count
 */
export const RoomImageModal = ({ roomId, onClose, onImagesChange }) => {
  const url = process.env.REACT_APP_SERVER_URL;
  const fileInputRef = useRef(null);

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  /* ── fetch images for this room ── */
  const fetchImages = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/images/getImagesByRoomId/${roomId}`);
      setImages(res.data);
    } catch (err) {
      setError("Failed to load images.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (roomId) fetchImages();
  }, [roomId]);

  /* ── upload handler ── */
  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Only image files are supported.");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("caption", "");

      // Step 1: upload to S3 + insert into image_details → returns { data: imageId }
      const uploadRes = await axios.post(`${url}/api/posts`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const imageId = uploadRes.data.data;

      // Step 2: link image to this room
      await axios.post(`${url}/addRoomImageRelation`, { imageId, roomId });

      await fetchImages();
      onImagesChange?.();
    } catch (err) {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  /* ── toggle marketing flag (optimistic — only ONE image can be marketing per room) ── */
  const handleToggleMarketing = async (image) => {
    const newValue = image.isMarketing ? 0 : 1;

    // Optimistic update: unmark any other marketing image, set/unset this one
    setImages((prev) =>
      prev.map((img) => {
        if (img.id === image.id) return { ...img, isMarketing: newValue };
        if (newValue === 1) return { ...img, isMarketing: 0 }; // unmark previous marketing image
        return img;
      })
    );

    try {
      await axios.patch(`${url}/images/setMarketingImage/${image.id}`, {
        isMarketing: newValue,
        roomId, // ← required so backend can target the correct relation row
      });
      // Let parent know so the card thumbnail strip refreshes too
      onImagesChange?.();
    } catch (err) {
      // Roll back on failure by refetching the real state
      await fetchImages();
      setError("Failed to update marketing flag.");
    }
  };

  /* ── delete image ── */
  const handleDelete = async (imageId) => {
    try {
      await axios.delete(`${url}/images/deleteRoomImageRelation/${imageId}`);
      await axios.delete(`${url}/images/deleteImageDetailsByImageId/${imageId}`);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      onImagesChange?.();
    } catch (err) {
      setError("Failed to delete image.");
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      {/* ── title bar ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.25,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Room Images
          </Typography>
          {images.length > 0 && (
            <Chip label={images.length} size="small" sx={{ height: 20 }} />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {uploading && <LinearProgress />}

      <DialogContent sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5, py: 0 }}>
            {error}
          </Alert>
        )}

        {/* ── drop zone ── */}
        <Box
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={{
            border: "1.5px dashed",
            borderColor: dragOver ? "primary.main" : "divider",
            borderRadius: 1.5,
            py: 1.5,
            mb: 1.5,
            textAlign: "center",
            cursor: uploading ? "not-allowed" : "pointer",
            bgcolor: dragOver ? "primary.50" : "grey.50",
            "&:hover": uploading ? {} : { borderColor: "primary.main" },
          }}
        >
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileInput}
            style={{ display: "none" }}
          />
          {uploading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Uploading…</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <UploadIcon fontSize="small" color="primary" />
              <Typography variant="body2">Drop an image or click to browse</Typography>
            </Box>
          )}
        </Box>

        {/* ── loading state ── */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : images.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <ImageIcon sx={{ fontSize: 32, color: "text.disabled", mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">No images yet.</Typography>
          </Box>
        ) : (
          <ImageGrid
            images={images}
            onToggleMarketing={handleToggleMarketing}
            onDelete={handleDelete}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose} size="small" variant="outlined">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Image grid — marketing image always shown first, all images same tile size ── */
const ImageGrid = ({ images, onToggleMarketing, onDelete }) => {
  const sorted = [...images].sort((a, b) => (b.isMarketing ? 1 : 0) - (a.isMarketing ? 1 : 0));
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
        gap: 1,
      }}
    >
      {sorted.map((img) => (
        <ImageTile
          key={img.id}
          image={img}
          onToggleMarketing={onToggleMarketing}
          onDelete={onDelete}
        />
      ))}
    </Box>
  );
};

/* ── Single image tile ── */
const ImageTile = ({ image, onToggleMarketing, onDelete }) => {
  const [hovered, setHovered] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setConfirmDelete(false); }}
      sx={{
        position: "relative",
        borderRadius: 1,
        overflow: "hidden",
        aspectRatio: "4/3",
        bgcolor: "grey.100",
        border: "2px solid",
        borderColor: image.isMarketing ? "warning.main" : "transparent",
      }}
    >
      <img
        src={image.imageUrl}
        alt={image.imageName}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />

      {/* Hover overlay */}
      {hovered && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.45)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 0.5,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title={image.isMarketing ? "Remove marketing flag" : "Mark as marketing image"}>
              <IconButton
                size="small"
                onClick={() => onToggleMarketing(image)}
                sx={{ color: image.isMarketing ? "warning.light" : "grey.300", p: 0.25 }}
              >
                {image.isMarketing ? (
                  <StarIcon sx={{ fontSize: 16 }} />
                ) : (
                  <StarBorderIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {confirmDelete ? (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 10, lineHeight: 1.6 }}
                  onClick={() => onDelete(image.id)}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: 10, lineHeight: 1.6, bgcolor: "grey.600" }}
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Tooltip title="Delete image">
                <IconButton
                  size="small"
                  onClick={() => setConfirmDelete(true)}
                  sx={{ color: "grey.300", p: 0.25, "&:hover": { color: "error.light" } }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}

      {/* Marketing badge (always visible) */}
      {image.isMarketing ? (
        <Box
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            bgcolor: "warning.main",
            borderRadius: "50%",
            width: 16,
            height: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <StarIcon sx={{ fontSize: 10, color: "#fff" }} />
        </Box>
      ) : null}
    </Box>
  );
};